import { useState } from 'react';
import { Button, ErrorNote, ui } from '@/components/ui';
import { accountService } from '@/services/api';

// Ownership proof: a link sent to an address at the business domain, or a DNS TXT record.
export function ClaimPanel({
  businessSlug,
  onClaimed,
}: {
  businessSlug: string;
  onClaimed: (businessId: string) => void | Promise<void>;
}) {
  const [method, setMethod] = useState<'DOMAIN_EMAIL' | 'DNS_TXT'>('DOMAIN_EMAIL');
  const [email, setEmail] = useState('');
  const [claim, setClaim] = useState<{
    claimId: string;
    dnsRecord?: { type: string; name: string; value: string };
  } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      setClaim(
        await accountService.startClaim({
          businessSlug,
          method,
          ...(method === 'DOMAIN_EMAIL' ? { email } : {}),
        }),
      );
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const checkDns = async () => {
    if (!claim) return;
    setBusy(true);
    setError(null);
    try {
      const r = await accountService.verifyClaim(claim.claimId);
      await onClaimed(r.businessId);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (claim && method === 'DOMAIN_EMAIL')
    return <p>Check {email} for a verification link. It expires in 72 hours.</p>;
  if (claim?.dnsRecord) {
    return (
      <div className={ui.stack}>
        <p>
          Add this TXT record to your domain&apos;s DNS, then check again. DNS changes can take a
          few minutes.
        </p>
        <div className={ui.code}>
          {claim.dnsRecord.type} {claim.dnsRecord.name} &quot;{claim.dnsRecord.value}&quot;
        </div>
        <ErrorNote error={error} />
        <Button onClick={() => void checkDns()} disabled={busy}>
          Check DNS record
        </Button>
      </div>
    );
  }
  return (
    <div className={ui.stack}>
      <fieldset className={`${ui.stack} ${ui.bare}`}>
        <legend className={ui.fieldLabel}>How do you want to verify?</legend>
        <label className={ui.row}>
          <input
            type="radio"
            name="method"
            checked={method === 'DOMAIN_EMAIL'}
            onChange={() => {
              setMethod('DOMAIN_EMAIL');
            }}
          />{' '}
          Email at my company domain
        </label>
        <label className={ui.row}>
          <input
            type="radio"
            name="method"
            checked={method === 'DNS_TXT'}
            onChange={() => {
              setMethod('DNS_TXT');
            }}
          />{' '}
          DNS TXT record
        </label>
      </fieldset>
      {method === 'DOMAIN_EMAIL' ? (
        <div className={ui.field}>
          <label htmlFor="claim-email">Your email at the company domain</label>
          <input
            id="claim-email"
            type="email"
            className={ui.input}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
        </div>
      ) : null}
      <ErrorNote error={error} />
      <Button onClick={() => void start()} disabled={busy || (method === 'DOMAIN_EMAIL' && !email)}>
        {method === 'DOMAIN_EMAIL' ? 'Send verification email' : 'Get DNS record'}
      </Button>
    </div>
  );
}
